import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export const TITLE_MAX_LINES = 2;
// The h5 theme line height (133.4%) in em: a title of N lines is N × this tall.
export const H5_LINE_HEIGHT_EM = 1.334;
const ELLIPSIS = '…';

export interface UseExpandableTitleResult {
  /** Attach to the title element. */
  titleRef: React.RefObject<HTMLHeadingElement>;
  /** True while the title is showing in full. */
  isExpanded: boolean;
  /** The part of a too-long title that fits in two lines beside an ellipsis; null
   * when the whole title fits (or is open) and nothing is cut. */
  visibleText: string | null;
  onToggle: () => void;
}

// Where `title` must be cut to fit `maxHeightPx` once an ellipsis follows it. The
// title is laid out in a hidden copy of `element` (same type, same width), so the
// cut falls where the real text would wrap rather than at a guessed character count.
const fitTitle = (element: HTMLElement, title: string, maxHeightPx: number): string | null => {
  const container = element.parentElement;
  if (!container) return null;

  const probe = element.cloneNode(false) as HTMLElement;
  probe.removeAttribute('data-testid');
  probe.style.cssText =
    'position:absolute;visibility:hidden;pointer-events:none;max-height:none;overflow:visible;' +
    `width:${container.clientWidth}px`;
  container.appendChild(probe);
  try {
    const fits = (text: string) => {
      probe.textContent = text;
      return probe.scrollHeight <= maxHeightPx + 1;
    };
    if (fits(title)) return null;

    // Largest prefix that still fits with the ellipsis after it.
    let low = 0;
    let high = title.length - 1;
    while (low < high) {
      const middle = Math.ceil((low + high) / 2);
      if (fits(title.slice(0, middle).trimEnd() + ELLIPSIS)) low = middle;
      else high = middle - 1;
    }
    return title.slice(0, low).trimEnd();
  } finally {
    container.removeChild(probe);
  }
};

// Tracks where a long title is cut at two lines, and whether the reader has opened
// it. A title that fits is never cut and shows no toggle. Does nothing (no measuring,
// no observer) while `enabled` is false, so a header that has not opted in pays nothing.
export const useExpandableTitle = (
  title: string | undefined,
  enabled = true
): UseExpandableTitleResult => {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleText, setVisibleText] = useState<string | null>(null);

  const measure = useCallback(() => {
    const element = titleRef.current;
    if (!enabled || !element || isExpanded || !title) return;
    const maxHeightPx =
      parseFloat(window.getComputedStyle(element).fontSize) * TITLE_MAX_LINES * H5_LINE_HEIGHT_EM;
    // Not measurable (no layout, or a font size that is not in px): show it whole.
    setVisibleText(Number.isFinite(maxHeightPx) ? fitTitle(element, title, maxHeightPx) : null);
  }, [enabled, isExpanded, title]);

  useLayoutEffect(() => {
    measure();
    const element = titleRef.current;
    // Width changes re-wrap the title, so re-measure. Absent in non-browser hosts.
    if (!enabled || !element || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [enabled, measure]);

  // A different artifact's title starts collapsed.
  useLayoutEffect(() => setIsExpanded(false), [title]);

  const onToggle = useCallback(() => setIsExpanded((current) => !current), []);

  return { titleRef, isExpanded, visibleText, onToggle };
};
